// src/components/common/ModernInput.jsx

import React, { forwardRef } from 'react';

const ModernInput = forwardRef(({
    label,
    name,
    value,
    onChange,
    placeholder,
    textarea,
    required,
    disabled,
    readOnly,
    type = 'text',
    helpText,
    className,
    autoComplete,
    labelStyle,
    borderColor,
    inputClassName,
    borderRadius,
    borderWidth,
    borderStyle,
    inputTextColor,
    inputBackgroundColor,
    min,
    max,
    step,
    ...rest
}, ref) => {
    const InputElement = textarea ? 'textarea' : 'input';

    const inputValue = (type === 'date' || type === 'time') ? (value || '') : (value === null || value === undefined ? '' : value);

    // ⭐ AESTHETIC UPGRADE: Padded, rounded-2xl, glassmorphism border ring focus for 2026 aesthetics
    const defaultInputClasses = `
        mt-1 block w-full px-4 py-3 border-2 rounded-2xl shadow-sm
        placeholder-gray-400 dark:placeholder-gray-500 
        text-gray-900 dark:text-gray-200 
        bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm
        border-gray-200 dark:border-gray-700
        focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
        dark:focus:ring-blue-500/20 dark:focus:border-blue-500
        transition-all duration-300 font-medium sm:text-base
        ${textarea ? 'h-32 resize-y' : ''}
        ${disabled ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-70' : ''}
    `;
    
    // Note: Inline styles will override Tailwind classes. It's better to manage styling via classes if possible.
    const dynamicInputStyle = {
        borderColor: borderColor || undefined,
        borderRadius: borderRadius || undefined,
        borderWidth: borderWidth ? `${borderWidth}px` : undefined,
        borderStyle: borderStyle || undefined,
        color: inputTextColor || undefined,
        backgroundColor: inputBackgroundColor || undefined,
    };

    const baseInputProps = {
        id: name,
        name: name,
        value: inputValue,
        onChange: onChange,
        placeholder: placeholder,
        required: required,
        disabled: disabled,
        readOnly: readOnly,
        ref: ref,
        autoComplete: autoComplete,
        className: `${inputClassName || defaultInputClasses}`,
        style: dynamicInputStyle,
        min: min,
        max: max,
        step: step,
        ...rest
    };

    if (!textarea) {
        baseInputProps.type = type;
    }

    return (
        <div className={`mb-4 ${className || ''}`}>
            {label && (
                <label htmlFor={name} className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" style={labelStyle}>
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <InputElement {...baseInputProps} />

            {helpText && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{helpText}</p>
            )}
        </div>
    );
});

export default ModernInput;